import { useTranslation } from "react-i18next";
import {
  immutableSelectionKey,
  type ImmutableReusableOption,
  type ImmutableReusableSelection,
  type LegacyAmbiguousReusablePin,
  rightsCaptureLabel,
  reusableKindLabel,
} from "../lib/reusable";
import { Badge } from "./Badge";
import { Text } from "./Text";
import { Checkbox } from "./Choices";

export interface ImmutableReusablePickerProps {
  label: string;
  description?: string;
  options: readonly ImmutableReusableOption[];
  value: readonly ImmutableReusableSelection[];
  onChange: (value: ImmutableReusableSelection[]) => void;
  emptyLabel: string;
  legacyPins?: readonly LegacyAmbiguousReusablePin[];
  disabled?: boolean;
}

/** Exact-version selector that never falls back to a mutable reusable root. */
export function ImmutableReusablePicker({
  label,
  description,
  options,
  value,
  onChange,
  emptyLabel,
  legacyPins = [],
  disabled = false,
}: ImmutableReusablePickerProps) {
  const { t } = useTranslation();
  const selectedIDs = new Set(value.map(immutableSelectionKey));

  return (
    <fieldset disabled={disabled}>
      <legend className="font-sans text-[12px] font-semibold text-ink">{label}</legend>
      {description ? (
        <Text variant="body" className="mt-1 text-[12px] text-ink-faint">{description}</Text>
      ) : null}
      {options.length > 0 || legacyPins.length > 0 ? (
        <div className="mt-3 grid gap-2">
          {options.map((option) => {
            const checked = selectedIDs.has(option.versionID);
            const optionDisabled = disabled || (!option.selectable && !checked);
            return (
              <Checkbox
                key={option.versionID}
                className="studio-choice-card items-start"
                  name="reusable_version_ids"
                  value={option.versionID}
                  checked={checked}
                  disabled={optionDisabled}
                  onChange={() => {
                    if (checked) {
                      onChange(value.filter((selection) => selection.versionID !== option.versionID));
                      return;
                    }
                    if (!option.selectable) return;
                    onChange([...value, reusableSelectionFromOption(option)]);
                  }}
                label={<span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="min-w-0 truncate font-sans text-[13px] font-medium text-ink">{option.name}</span>
                    <Badge tone="soft" size="sm">{reusableKindLabel(t, option.kind)}</Badge>
                    <Badge tone="outline" size="sm">
                      {t("foundation.reusable.versionNumber", { number: option.versionNumber })}
                    </Badge>
                    {option.displayNameStatus === "legacy_ambiguous" ? (
                      <Badge tone="outline" size="sm">
                        {t("foundation.reusable.legacyAmbiguousDisplayName")}
                      </Badge>
                    ) : null}
                  </span>
                  <span className="mt-1 flex flex-wrap gap-x-2 gap-y-1 font-sans text-[11.5px] text-ink-faint">
                    <span>{option.versionStatus.replaceAll("_", " ")}</span>
                    {option.rightsStatus ? (
                      <span>{t("foundation.reusable.rightsStatus", { status: option.rightsStatus.replaceAll("_", " ") })}</span>
                    ) : null}
                    {option.rightsCaptureStatus === "legacy_ambiguous" ? (
                      <span className="text-danger">
                        {rightsCaptureLabel(t, option.rightsCaptureStatus)}
                      </span>
                    ) : null}
                    {option.unavailableReason ? <span className="text-danger">{option.unavailableReason}</span> : null}
                  </span>
                </span>}
              />
            );
          })}
          {legacyPins.map((pin) => (
            <div
              key={`${pin.reusableID}:${pin.legacyRootRevision}:${pin.role}`}
              aria-disabled="true"
              className="flex min-h-12 items-start gap-3 rounded-md border border-line bg-canvas-soft px-3 py-2.5 opacity-75"
            >
              <span aria-hidden="true" className="mt-0.5 size-4 rounded-sm border border-line-strong" />
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 truncate font-sans text-[13px] font-medium text-ink">
                    {pin.name ?? t("foundation.reusable.unknownLegacyName")}
                  </span>
                  <Badge tone="soft" size="sm">{reusableKindLabel(t, pin.kind)}</Badge>
                  <Badge tone="outline" size="sm">{t("foundation.reusable.legacyAmbiguous")}</Badge>
                </span>
                <span className="mt-1 block font-sans text-[11.5px] text-danger">
                  {t("foundation.reusable.legacyAmbiguousHint", { revision: pin.legacyRootRevision ?? t("common.unknown") })}
                </span>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <Text variant="body" className="mt-2 text-[12px] text-ink-faint">{emptyLabel}</Text>
      )}
    </fieldset>
  );
}

function reusableSelectionFromOption(option: ImmutableReusableOption): ImmutableReusableSelection {
  return {
    reusableID: option.reusableID,
    versionID: option.versionID,
    versionNumber: option.versionNumber,
    kind: option.kind,
    name: option.name,
    displayNameStatus: option.displayNameStatus,
    role: option.role,
  };
}
